import {ContentContainer, useMessage} from '@coolcolduk/ui-mui';
import {QuizSessionFeedback} from '@cp/model';
import {Stack} from '@mui/material';
import {useNavigate, useParams} from 'react-router';
import QuestionsReview from '../../components/QuestionsReview';
import useGetExamModule from '../../hooks/examModules/useGetExamModule';
import useRetryQuiz from '../../hooks/quizSession/useRetryQuiz';
import useSubscribeQuizSession from '../../hooks/quizSession/useSubscribeQuizSession';
import useUpdateQuizSessionFeedback from '../../hooks/quizSession/useUpdateQuizSessionFeedback';
import LoadingPage from '../other/LoadingPage';
import SessionCompleteActions from './SessionCompleteActions';
import SessionCompleteBreakdown from './SessionCompleteBreakdown';
import SessionCompleteFeedback from './SessionCompleteFeedback';
import SessionCompleteDetails from './SessionCompleteDetails';
import SessionCompleteHeader from './SessionCompleteHeader';
import SessionCompleteMessage from './SessionCompleteMessage';
import SessionCompleteScore from './SessionCompleteScore';

export default function SessionCompletePage() {
  const navigate = useNavigate();
  const {sessionId} = useParams<{sessionId: string}>();
  const {retryQuiz, loading: retryLoading} = useRetryQuiz();
  const {updateQuizSessionFeedback, loading: feedbackLoading} = useUpdateQuizSessionFeedback();
  const {showMessage} = useMessage();

  // Fetch session data using the sessionId from URL params
  const {session, loading: sessionLoading} = useSubscribeQuizSession(sessionId || '');

  // Fetch full module with all topics for TopicSelection component
  const {examModule, loading: moduleLoading} = useGetExamModule(session?.moduleId ?? '', {skip: !session?.moduleId});

  if (sessionLoading || moduleLoading) {
    return <LoadingPage minusAppBar />;
  }

  if (!session) {
    return (
      <ContentContainer data-testid="session-complete-page">
        <Stack spacing={4}>
          <SessionCompleteHeader />
          <SessionCompleteMessage score={0} />
        </Stack>
      </ContentContainer>
    );
  }

  const totalQuestions = session.questions.length;
  const correctAnswers = session.questions.filter((q) => q.isCorrect).length;
  const score =
    'score' in session && typeof session.score === 'number'
      ? session.score
      : Math.round((correctAnswers / totalQuestions) * 100);
  const incorrectAnswers = totalQuestions - correctAnswers;

  const sessionWithScore = {...session, score};

  // Use all topics from the module (not just selected topics) for TopicSelection
  const moduleTopics = examModule?.topics || [];

  const handleBackToDashboard = () => {
    navigate('/');
  };

  const handleStartNewSession = () => {
    navigate('/session/new');
  };

  const handleRetry = async () => {
    if (!session) return;
    try {
      const newSession = (await retryQuiz(session.moduleId, session.id)) as {id: string} | null | undefined;
      if (newSession?.id) {
        navigate(`/session/${newSession.id}`);
      }
    } catch {
      showMessage('Failed to retry session', 3000, 'error');
    }
  };

  const handleFeedbackSelect = async (feedback: QuizSessionFeedback) => {
    if (!session?.id) return;
    if (session.feedback) return;
    try {
      await updateQuizSessionFeedback(session.id, feedback);
      showMessage('Thanks for your feedback!', 2000, 'success');
    } catch {
      showMessage('Failed to submit feedback', 3000, 'error');
    }
  };

  return (
    <ContentContainer data-testid="session-complete-page">
      <Stack spacing={4}>
        <SessionCompleteHeader />

        <SessionCompleteScore score={score} />

        <SessionCompleteBreakdown
          correctAnswers={correctAnswers}
          incorrectAnswers={incorrectAnswers}
          totalQuestions={totalQuestions}
        />

        <SessionCompleteMessage score={score} />

        <SessionCompleteFeedback
          feedback={session.feedback}
          loading={feedbackLoading}
          onSelect={handleFeedbackSelect}
        />

        <SessionCompleteDetails
          session={sessionWithScore as import('@cp/model').QuizSession}
          moduleTopics={moduleTopics}
        />

        <QuestionsReview session={sessionWithScore as import('@cp/model').QuizSession} />

        <SessionCompleteActions
          onBackToDashboard={handleBackToDashboard}
          onStartNewSession={handleStartNewSession}
          onRetry={handleRetry}
          retryLoading={retryLoading}
        />
      </Stack>
    </ContentContainer>
  );
}
